/** Small shared UI pieces: toast, hover tooltip ([data-tipx]), modal shell, source dot. Same markup/classes as the original. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Src } from "@ic/engine";
import { useStore } from "../../store.ts";
import { useT } from "../i18n.ts";

export function Toast() {
  const msg = useStore((s) => s.toastMsg);
  const seq = useStore((s) => s.toastSeq);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!seq) return;
    setOn(true);
    const t = setTimeout(() => setOn(false), 2600);
    return () => clearTimeout(t);
  }, [seq]);
  return <div className={"toast" + (on ? " on" : "")}>{msg}</div>;
}

/** One tooltip for the whole page, driven by data-tipx attributes (like the original). */
export function Tooltip() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tp = ref.current!;
    const over = (e: MouseEvent) => {
      const d = (e.target as Element).closest?.("[data-tipx]");
      if (!d) { tp.style.display = "none"; return; }
      const r = d.getBoundingClientRect();
      tp.textContent = d.getAttribute("data-tipx");
      tp.style.display = "block";
      const w = tp.offsetWidth;
      tp.style.left = Math.max(8, Math.min(r.left + 12, window.innerWidth - w - 10)) + "px";
      tp.style.top = (r.top - tp.offsetHeight - 8 < 0 ? r.bottom + 8 : r.top - tp.offsetHeight - 8) + "px";
    };
    document.addEventListener("mouseover", over);
    return () => document.removeEventListener("mouseover", over);
  }, []);
  return <div className="tip" ref={ref} />;
}

/** Modal shell: click on the backdrop or Esc closes it. */
export function Modal({ children, onClose, wide }: { children: ReactNode; onClose(): void; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="mbg on" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={wide ? { maxWidth: 1000 } : undefined}>{children}</div>
    </div>
  );
}

export function SrcDot({ s }: { s: Src | null | undefined }) {
  const t = useT();
  if (!s) return <i className="src none" data-tipx={t("No value yet", "尚未填写")} />;
  const lab = { ai: t("AI estimate", "AI 估"), hist: t("Historical", "历史"), man: t("Typed by you", "手填") }[s.t];
  return <i className={"src " + s.t} data-tipx={lab + (s.d ? " · " + s.d : "")} />;
}

/**
 * Text input that keeps what is being typed and commits on every change. For quote overrides an emptied field
 * falls back to the default when it loses focus (偏差 D4) — `display` is what to show when not editing.
 */
export function Field(props: {
  value: string; display?: string; onChange(v: string): void; onBlurEmpty?(): void;
  className?: string; placeholder?: string; dataK?: string; kind?: string; i?: number;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const shown = editing ?? props.display ?? props.value;
  return (
    <input
      className={props.className} placeholder={props.placeholder} title={shown} value={shown}
      data-k={props.dataK} data-kind={props.kind} data-i={props.i}
      onFocus={() => setEditing(props.display ?? props.value)}
      onChange={(e) => { setEditing(e.target.value); props.onChange(e.target.value); }}
      onBlur={() => { if (editing === "" && props.onBlurEmpty) props.onBlurEmpty(); setEditing(null); }}
    />
  );
}
